import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Cpu, Zap, Maximize2, MonitorSmartphone, Star, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const TYPE_COLORS = {
  diode:  { pill: 'bg-blue-600',   label: 'Diode' },
  co2:    { pill: 'bg-green-600',  label: 'CO2' },
  fibre:  { pill: 'bg-purple-600', label: 'Fibre' },
  galvo:  { pill: 'bg-orange-600', label: 'Galvo' },
};

const LEVEL_COLORS = {
  beginner:     'text-green-400',
  intermediate: 'text-yellow-400',
  advanced:     'text-red-400',
};

const LaserMachinesCatalog = () => {
  const [machines, setMachines]       = useState([]);
  const [activeType, setActiveType]   = useState('all');
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    fetchMachines('all');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMachines = async (type) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (type && type !== 'all') params.set('laser_type', type);
      const res = await axios.get(`${API}/laser/catalog?${params}`);
      setMachines(res.data || []);
    } catch (err) {
      console.error('Failed to load laser catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    fetchMachines(type);
  };

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="laser-machines-catalog" style={bgStyle(IMAGES.LASER_MACHINES_CATALOG)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/laser" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Cpu className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">HARDWARE</div>
            <h1 className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Machines Catalog
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              Diode · CO2 · Fibre · Galvo — specs, software, and use cases
            </p>
          </div>
        </div>
      </div>

      {/* Type filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'diode', 'co2', 'fibre', 'galvo'].map(type => (
          <button
            key={type}
            onClick={() => handleTypeChange(type)}
            className={`px-3 py-1.5 rounded text-xs font-semibold uppercase transition-colors ${
              activeType === type
                ? 'bg-orange-500 text-black'
                : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            {type === 'all' ? 'All Types' : (TYPE_COLORS[type]?.label || type)}
          </button>
        ))}
      </div>

      {/* Machine count */}
      {!loading && (
        <p className="text-zinc-500 text-xs mb-4">{machines.length} machine{machines.length !== 1 ? 's' : ''} listed</p>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Cpu className="w-10 h-10 text-orange-500 animate-pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {machines.map((m) => {
            const typeInfo = TYPE_COLORS[m.laser_type] || { pill: 'bg-zinc-600', label: m.laser_type };
            const levelColor = LEVEL_COLORS[m.experience_level] || 'text-zinc-400';

            return (
              <Card key={m.id} className="bg-zinc-900 border-zinc-800 hover:border-orange-500/40 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase text-white ${typeInfo.pill}`}>
                      {typeInfo.label}
                    </span>
                    <span className={`text-xs font-semibold uppercase ${levelColor}`}>
                      {m.experience_level}
                    </span>
                  </div>
                  <CardTitle className="text-white text-base leading-snug">
                    {m.brand} <span className="text-orange-400">{m.model}</span>
                  </CardTitle>
                  {m.description && (
                    <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{m.description}</p>
                  )}
                </CardHeader>

                <CardContent className="space-y-3">
                  {/* Specs row */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-zinc-800 rounded p-2">
                      <Zap className="w-3 h-3 text-orange-400 mx-auto mb-0.5" />
                      <div className="text-white text-xs font-bold">
                        {m.power_watts > 0 ? `${m.power_watts}W` : '—'}
                      </div>
                      <div className="text-zinc-500 text-[10px]">Power</div>
                    </div>
                    <div className="bg-zinc-800 rounded p-2">
                      <Maximize2 className="w-3 h-3 text-orange-400 mx-auto mb-0.5" />
                      <div className="text-white text-xs font-bold">
                        {m.bed_width > 0 ? `${m.bed_width}×${m.bed_height}` : '—'}
                      </div>
                      <div className="text-zinc-500 text-[10px]">Bed (mm)</div>
                    </div>
                    <div className="bg-zinc-800 rounded p-2">
                      <Cpu className="w-3 h-3 text-orange-400 mx-auto mb-0.5" />
                      <div className="text-white text-xs font-bold truncate">{m.controller || '—'}</div>
                      <div className="text-zinc-500 text-[10px]">Controller</div>
                    </div>
                  </div>

                  {/* Safety class */}
                  {m.safety_class && (
                    <div className="flex items-start gap-2 text-xs text-zinc-400">
                      <MonitorSmartphone className="w-3.5 h-3.5 mt-0.5 text-zinc-500 shrink-0" />
                      <span>{m.safety_class}</span>
                    </div>
                  )}

                  {/* Software */}
                  {m.software?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {m.software.map((sw, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded text-[10px]">
                          {sw}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Strengths */}
                  {m.strengths?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-green-400 text-[10px] font-bold uppercase mb-1">
                        <Star className="w-3 h-3" /> Strengths
                      </div>
                      <ul className="space-y-0.5">
                        {m.strengths.map((s, i) => (
                          <li key={i} className="text-zinc-400 text-xs flex items-start gap-1.5">
                            <span className="text-green-500 mt-0.5">·</span>{s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Limitations */}
                  {m.limitations?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-red-400 text-[10px] font-bold uppercase mb-1">
                        <AlertCircle className="w-3 h-3" /> Limitations
                      </div>
                      <ul className="space-y-0.5">
                        {m.limitations.map((l, i) => (
                          <li key={i} className="text-zinc-400 text-xs flex items-start gap-1.5">
                            <span className="text-red-500 mt-0.5">·</span>{l}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && machines.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <Cpu className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No machines found for this filter.</p>
        </div>
      )}
    </div>
  );
};

export default LaserMachinesCatalog;
